/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import SvgIcon from "./SvgIcon";

export type InputProps = React.ComponentPropsWithRef<"input"> & {
  label?: string;
  info?: string;
  required?: boolean;
  containerProps?: React.ComponentPropsWithoutRef<"div">;
  errorMessage?: string;
};

const Input = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const { label, info, required, errorMessage, containerProps, type, style, ...rest } = props;
  const invalid = !!errorMessage;

  const isPassword = type === "password";
  const [reveal, setReveal] = React.useState(false);
  const inputType = isPassword && reveal ? "text" : type;

  return (
    <div className={`${label && "mb-3"} ${invalid && "field-group--invalid"}`} {...containerProps}>
      {label && <label className="label">{label}</label>}
      {required && <sup style={{ color: "red" }}>*</sup>}
      {info && (
        <span
          className="help"
          data-bs-toggle="tooltip"
          data-trigger="hover"
          data-placement="right"
          data-html="true"
          data-container="body"
          title={info}
        >
          <SvgIcon name="question-circle" className="ml-1 label" />
        </span>
      )}
      <div style={{ position: "relative" }}>
        <input
          aria-invalid={invalid ? "true" : "false"}
          className="field"
          ref={ref}
          type={inputType}
          style={isPassword ? { paddingRight: "2.75rem", ...style } : style}
          {...rest}
        />
        {isPassword && (
          <button
            type="button"
            tabIndex={-1}
            aria-label={reveal ? "Hide password" : "Show password"}
            onClick={() => setReveal((value) => !value)}
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "2.75rem",
              height: "100%",
              padding: 0,
              border: 0,
              background: "transparent",
              color: "#6c757d",
              fontSize: "1.25rem",
              lineHeight: 1,
              cursor: "pointer"
            }}
          >
            <SvgIcon name={reveal ? "eye-slash" : "eye"} />
          </button>
        )}
      </div>
      {invalid && (
        <div className="field-helper">
          <label>{errorMessage}</label>
        </div>
      )}
    </div>
  );
});

Input.displayName = "Input";

export default Input;
